import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useEscape } from "@/lib/useEscape";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import {
  BarChart3,
  Bell,
  BookOpenText,
  Boxes,
  ChevronDown,
  ClipboardList,
  CreditCard,
  UserRound,
  Users,
  MessageCircleQuestion,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  PlugZap,
  RotateCcw,
  Settings,
  Sun,
  Truck,
  X,
  Link2,
  Layers,
  ListChecks,
  type LucideIcon,
} from "lucide-react";
import type { ChannelKey, ModuleKey, PermissionKey } from "@theadam/shared";
import { ChannelLogo } from "@/components/ChannelLogo";
import { StoreLogo, TheAdamMark } from "@/components/brand";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { useLogout, useMe } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { LiveAlerts } from "@/features/notifications/LiveAlerts";
import { FeedbackButton } from "@/features/feedback/FeedbackButton";
import { useNotifications } from "./NotificationsPage";
import { cn } from "@/lib/utils";
import { IyzicoMark } from "@/components/IyzicoMark";

interface NavItem {
  label: string;
  icon: LucideIcon;
  to: string;
  permission: PermissionKey;
  module?: ModuleKey;
  badge?: "notifications" | "questions";
  end?: boolean;
  /** Kanal logosu (ikon yerine). */
  logo?: ChannelKey;
  /** Ödeme sağlayıcısı işareti (ikon yerine). */
  mark?: "iyzico";
  /**
   * Yalnız bu bağlantı aktifken görünür (Entegrasyonlar'da bağlanıp durdurulmamış):
   * kanal anahtarı, "iyzico" ya da "multi" (en az iki kanal; ürün eşleştirme).
   */
  requires?: ChannelKey | "iyzico" | "multi";
}

const SECTIONS: { title: string; items: NavItem[] }[] = [
  { title: "Genel", items: [{ label: "Genel Bakış", icon: LayoutDashboard, to: "/", permission: "dashboard:view", end: true }] },
  {
    title: "Satış",
    items: [
      { label: "Tüm Siparişler", icon: ClipboardList, to: "/siparisler", permission: "orders:view", module: "orders" },
      { label: "Kargo", icon: Truck, to: "/kargo", permission: "shipments:view", module: "shipments" },
      { label: "İade / İptaller", icon: RotateCcw, to: "/iadeler", permission: "returns:view", module: "orders" },
      { label: "Müşteri soruları", icon: MessageCircleQuestion, to: "/sorular", permission: "orders:view", module: "orders", badge: "questions" },
      { label: "Müşteriler", icon: Users, to: "/musteriler", permission: "orders:view_customer", module: "orders" },
      { label: "İyzico ödemeleri", icon: CreditCard, mark: "iyzico", to: "/odemeler/iyzico", permission: "pricing:view_cost", requires: "iyzico" },
    ],
  },
  {
    title: "Katalog",
    items: [
      { label: "Tüm ürünler", icon: Layers, to: "/urunler/tumu", permission: "products:view", module: "products" },
      { label: "IdeaSoft ürünleri", icon: Boxes, logo: "ideasoft", to: "/urunler/ideasoft", permission: "products:view", module: "products", requires: "ideasoft" },
      { label: "Trendyol ürünleri", icon: Boxes, logo: "trendyol", to: "/urunler/trendyol", permission: "products:view", module: "products", requires: "trendyol" },
      { label: "Ürün eşleştirme", icon: Link2, to: "/urunler/eslestirme", permission: "products:view", module: "products", requires: "multi" },
      { label: "Eksik bilgiler", icon: ListChecks, to: "/urunler/eksikler", permission: "products:view", module: "products" },
    ],
  },
  {
    title: "Analiz",
    items: [
      { label: "Raporlar", icon: BarChart3, to: "/raporlar", permission: "reports:view", module: "reports" },
      { label: "Bildirimler", icon: Bell, to: "/bildirimler", permission: "notifications:view", module: "notifications", badge: "notifications" },
    ],
  },
  {
    title: "Sistem",
    items: [
      { label: "Entegrasyonlar", icon: PlugZap, to: "/ayarlar/entegrasyonlar", permission: "integrations:view" },
      { label: "Ayarlar", icon: Settings, to: "/ayarlar", permission: "dashboard:view", end: true },
      { label: "Sürüm notları", icon: BookOpenText, to: "/sistem/surumler", permission: "dashboard:view" },
    ],
  },
];

function NavItemLink({ item, unread, questions, onNavigate }: { item: NavItem; unread: number; questions: number; onNavigate: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-2.5 rounded-lg px-2 py-1 text-[0.86rem] transition-all outline-none focus-visible:ring-2 focus-visible:ring-accent/70 [@media(min-height:900px)]:py-1.5",
          isActive ? "bg-white/[0.07] font-semibold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]" : "text-sidebar-foreground/70 hover:bg-white/[0.04] hover:text-white",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span aria-hidden className="absolute inset-y-1.5 -left-4 w-[3px] rounded-r-full bg-accent shadow-[0_0_12px_var(--accent)]" />}
          <span
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-md transition-colors",
              isActive ? "bg-gradient-to-br from-accent to-accent/70 text-accent-foreground shadow-md shadow-accent/20" : "bg-white/[0.04] text-sidebar-muted group-hover:text-white",
            )}
          >
            {item.mark === "iyzico" ? <IyzicoMark /> : item.logo ? <ChannelLogo channel={item.logo} size="sm" /> : <item.icon className="size-[15px]" aria-hidden />}
          </span>
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge === "questions" && questions > 0 && (
            <span className="min-w-5 rounded-full bg-warning px-1.5 text-center text-[11px] leading-5 font-bold text-white" aria-label={`${questions} cevap bekleyen soru`}>
              {questions > 99 ? "99+" : questions}
            </span>
          )}
          {item.badge === "notifications" && unread > 0 && (
            <span className="min-w-5 rounded-full bg-accent px-1.5 text-center text-[11px] leading-5 font-bold text-accent-foreground" aria-label={`${unread} okunmamış`}>
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function SidebarNav({ onNavigate }: { onNavigate: () => void }) {
  const { data: me } = useMe();
  const { data: notes } = useNotifications();
  const unread = notes?.unread ?? 0;
  // Cevap bekleyen Trendyol soruları (60 sn önbellekli uç; bağlantı yoksa null).
  const qna = useQuery({ queryKey: ["qna-summary"], queryFn: () => api.get<{ waiting: number | null }>("/qna/summary"), refetchInterval: 120_000, enabled: Boolean(me?.permissions.includes("orders:view")) });
  const questions = qna.data?.waiting ?? 0;
  const connected = (r: NavItem["requires"]) => !r || !me || (r === "iyzico" ? me.connections.iyzico : r === "multi" ? me.connections.channels.length > 1 : me.connections.channels.includes(r));
  const visible = (items: NavItem[]) => items.filter((i) => me?.permissions.includes(i.permission) && (!i.module || me.enabledModules.includes(i.module)) && connected(i.requires));
  return (
    <div className="space-y-2.5 [@media(min-height:900px)]:space-y-3.5">
      {SECTIONS.map((sec) => {
        const items = visible(sec.items);
        if (!items.length) return null;
        return (
          <div key={sec.title}>
            <p className="px-2 pb-0.5 text-[10px] font-semibold tracking-[0.22em] text-sidebar-muted/80 uppercase">{sec.title}</p>
            <ul className="space-y-px">
              {items.map((item) => (
                <li key={item.to}>
                  <NavItemLink item={item} unread={unread} questions={questions} onNavigate={onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function NotificationBell() {
  const { data } = useNotifications();
  const unread = data?.unread ?? 0;
  return (
    <NavLink
      to="/bildirimler"
      aria-label={unread ? `Bildirimler, ${unread} okunmamış` : "Bildirimler"}
      className="relative inline-flex size-10 items-center justify-center rounded-full border bg-card text-foreground/80 shadow-sm hover:text-foreground"
    >
      <Bell className="size-[18px]" />
      {unread > 0 && <span className="absolute top-1.5 right-1.5 size-2.5 rounded-full bg-accent ring-2 ring-card" aria-hidden />}
    </NavLink>
  );
}

/** Sağ üst kullanıcı menüsü: şifre, tema, çıkış. */
function UserMenu() {
  const { data: me } = useMe();
  const logout = useLogout();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => root.current && !root.current.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  const item = "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted";
  return (
    <div ref={root} className="relative">
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="flex items-center gap-3 rounded-full border bg-card py-1 pr-3 pl-1 shadow-sm hover:bg-muted/60">
        <Avatar name={me?.user.name} url={me?.user.avatarUrl} tone="primary" className="size-9 text-sm ring-2 ring-accent/40" />
        <span className="hidden text-left leading-tight sm:block">
          <span className="block text-sm font-semibold">{me?.user.name}</span>
          <span className="block text-xs text-muted-foreground">{me?.role.name}</span>
        </span>
        <ChevronDown className="size-4 text-muted-foreground" aria-hidden />
      </button>
      {open && (
        <div role="menu" className="absolute top-full right-0 z-40 mt-2 w-56 rounded-xl border bg-card p-1.5 shadow-xl shadow-black/10">
          <NavLink to="/ayarlar?sekme=sifre" role="menuitem" className={item} onClick={() => setOpen(false)}>
            <UserRound className="size-4 text-muted-foreground" /> Profilim ve şifre
          </NavLink>
          <button type="button" role="menuitem" className={item} onClick={toggle}>
            {theme === "dark" ? <Sun className="size-4 text-muted-foreground" /> : <Moon className="size-4 text-muted-foreground" />}
            {theme === "dark" ? "Açık tema" : "Koyu tema"}
          </button>
          <div className="my-1 border-t" />
          <button type="button" role="menuitem" className={cn(item, "text-danger")} onClick={logout}>
            <LogOut className="size-4" /> Çıkış
          </button>
        </div>
      )}
    </div>
  );
}

export function AppShell() {
  const { data: me } = useMe();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEscape(() => setOpen(false), open);

  const sidebar = (
    <div className="relative flex h-full flex-col overflow-hidden">
      {/* Hafif altın parıltı ve ince doku: görsel yok, yalnız ışık. */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -left-16 size-72 rounded-full bg-accent/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" />

      <Link to="/" aria-label="Genel Bakış" onClick={() => setOpen(false)} className="relative mx-6 mt-5 flex w-fit flex-col items-start transition-opacity hover:opacity-90">
        <TheAdamMark tone="onDark" className="h-[17px]" />
        <span className="mt-1.5 text-[8.5px] font-semibold tracking-[0.42em] text-sidebar-muted uppercase">E-Commerce</span>
      </Link>

      <div className="relative mx-4 mt-4 flex items-center justify-between gap-3 rounded-xl border border-accent/20 bg-gradient-to-b from-white/[0.06] to-white/[0.02] px-3 py-2 [@media(max-height:760px)]:hidden">
        <StoreLogo tone="onDark" className="h-7 w-auto max-w-[150px] object-contain object-left" />
        <span className="flex items-center gap-1.5 text-[9.5px] tracking-[0.16em] text-sidebar-muted uppercase" title="Çalışma alanı">
          <span className="size-1.5 rounded-full bg-success shadow-[0_0_8px_var(--success)]" aria-hidden />
          Aktif
        </span>
        <span className="sr-only">Çalışma alanı: {me?.tenant.name}</span>
      </div>

      <nav className="relative mt-4 min-h-0 flex-1 overflow-y-auto px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Ana menü">
        <SidebarNav onNavigate={() => setOpen(false)} />
      </nav>

      <div className="relative m-3 mt-1 flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.04] p-2">
        <Link to="/ayarlar?sekme=sifre" title="Profilim" className="shrink-0 rounded-full focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none">
          <Avatar name={me?.user.name} url={me?.user.avatarUrl} className="size-8 text-sm ring-2 ring-white/10" />
        </Link>
        <span className="min-w-0 flex-1 leading-tight">
          <span className="block truncate text-sm font-semibold text-white">{me?.user.name}</span>
          <span className="block truncate text-xs text-sidebar-muted">{me?.role.name}</span>
        </span>
        <button type="button" onClick={logout} aria-label="Çıkış" title="Çıkış" className="flex size-9 items-center justify-center rounded-lg text-sidebar-muted hover:bg-white/[0.08] hover:text-white">
          <LogOut className="size-[18px]" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh bg-background lg:grid lg:grid-cols-[272px_1fr]">
      <a href="#icerik" className="sr-only z-50 rounded-lg bg-primary px-4 py-2 text-primary-foreground focus:not-sr-only focus:fixed focus:top-3 focus:left-3">
        İçeriğe geç
      </a>
      <aside className="sticky top-0 hidden h-dvh bg-gradient-to-b from-sidebar to-[oklch(0.15_0.032_262)] text-sidebar-foreground lg:block">{sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Menü">
          <button type="button" className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} aria-label="Menüyü kapat" />
          <aside className="absolute inset-y-0 left-0 w-72 bg-gradient-to-b from-sidebar to-[oklch(0.15_0.032_262)] text-sidebar-foreground shadow-xl">{sidebar}</aside>
        </div>
      )}
      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 bg-background/85 px-4 py-3 backdrop-blur-md sm:px-8">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen((v) => !v)} aria-label="Menüyü aç">
            {open ? <X /> : <Menu />}
          </Button>
          <div className="flex-1" />
          <LiveAlerts />
          <NotificationBell />
          <UserMenu />
        </header>
        <main id="icerik" tabIndex={-1} className="flex-1 px-4 pt-2 pb-10 outline-none sm:px-8">
          <Outlet />
        </main>
        <FeedbackButton />
      </div>
    </div>
  );
}
